import { Injectable } from '@angular/core';
import { catchError, map, type Observable } from 'rxjs';
// eslint-disable-next-line @typescript-eslint/consistent-type-imports
import { HttpClient } from '@angular/common/http';
import { environment } from '../../../environments/environment';

@Injectable({
  providedIn: 'root',
})
export class CryptoService {
  constructor(private readonly http: HttpClient) {}

  decrypt(cypherText: string): Observable<any> {
    const req = {
      Text: cypherText,
    };
    const url = environment.OASUrl() + '/crypto/decrypt';

    return this.http.post(url, req).pipe(
      catchError((err) => {
        throw err;
      }),
      map((data: any) => {
        return data;
      }),
    );
  }

  /**
   * Encrypts text using AES-CBC with HMAC-SHA256 for authentication
   * @param keyInput - Encryption key (hex string or UTF-8 string). Must be 16, 24, or 32 bytes.
   * @param text - Plain text to encrypt
   * @returns Promise<string> - Hex string containing IV + Ciphertext + HMAC
   * @throws Error if key or text is invalid, or if encryption fails
   */
  async encryptToHex(keyInput: string, text: string): Promise<string> {
    const keyTrimmed = keyInput.trim();

    if (!keyTrimmed) throw new Error('Please provide a key');
    if (!text) throw new Error('Please provide text');

    // Step 1: key bytes (hex -> raw bytes; else utf8 bytes)
    const keyBytes: Uint8Array<ArrayBuffer> = this.isHex(keyTrimmed)
      ? this.hexToBytes(keyTrimmed)
      : this.utf8Encode(keyTrimmed);

    // AES key length must be 16/24/32 bytes
    if (![16, 24, 32].includes(keyBytes.length)) {
      throw new Error(`Invalid AES key length: ${keyBytes.length} bytes. Must be 16, 24, or 32.`);
    }

    // Step 2: IV (16 bytes)
    const iv = new Uint8Array(16);
    crypto.getRandomValues(iv);
    const ivHex = this.bytesToHexUpper(iv);

    // Step 3: zero pad to 16-byte multiple
    const plainBytes = this.utf8Encode(text);
    const padded = this.zeroPadToBlock(plainBytes, 16);

    // Step 4: AES-CBC encrypt
    const aesKey = await crypto.subtle.importKey('raw', keyBytes, { name: 'AES-CBC' }, false, ['encrypt']);

    const cipherBuf = await crypto.subtle.encrypt({ name: 'AES-CBC', iv }, aesKey, padded);
    const cipherHex = this.bytesToHexUpper(new Uint8Array(cipherBuf));

    // Step 5: HMAC-SHA256 over ivHex + cipherHex (as UTF-8 bytes)
    const hmacKey = await crypto.subtle.importKey('raw', keyBytes, { name: 'HMAC', hash: { name: 'SHA-256' } }, false, [
      'sign',
    ]);

    const macInput = this.utf8Encode(ivHex + cipherHex);
    const macBuf = await crypto.subtle.sign('HMAC', hmacKey, macInput);
    const macHex = this.bytesToHexUpper(new Uint8Array(macBuf));

    // Final: <IVHEX><CIPHERHEX><HMACHEX>
    return ivHex + cipherHex + macHex;
  }

  // ---------- Private Utilities ----------

  /**
   * Converts a UTF-8 string to Uint8Array
   */
  private utf8Encode(str: string): Uint8Array<ArrayBuffer> {
    return new TextEncoder().encode(str);
  }

  /**
   * Converts Uint8Array to uppercase hex string
   */
  private bytesToHexUpper(bytes: Uint8Array): string {
    let out = '';
    for (let i = 0; i < bytes.length; i++) {
      out += bytes[i].toString(16).padStart(2, '0');
    }
    return out.toUpperCase();
  }

  /**
   * Converts hex string to Uint8Array
   */
  private hexToBytes(hex: string): Uint8Array<ArrayBuffer> {
    const out = new Uint8Array(hex.length / 2);
    for (let i = 0; i < out.length; i++) {
      out[i] = parseInt(hex.substring(i * 2, i * 2 + 2), 16);
    }
    return out;
  }

  /**
   * Checks if a string is a valid hex string
   */
  private isHex(s: string): boolean {
    return /^[0-9a-fA-F]+$/.test(s) && s.length % 2 === 0;
  }

  /**
   * Pads data to a block size multiple using zero padding
   * Note: Zero padding is not cryptographically secure. Consider using PKCS#7 padding.
   */
  private zeroPadToBlock(data: Uint8Array, blockSize: number): Uint8Array<ArrayBuffer> {
    const rem = data.length % blockSize;
    if (rem === 0) return data as Uint8Array<ArrayBuffer>;

    const pad = blockSize - rem;
    const out = new Uint8Array(data.length + pad);
    out.set(data, 0);
    return out; // zero-filled padding
  }
}
